import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useMemo, useState} from 'react';

import {pluralize} from 'BenchlingUtil';
import {
  BaseActionFunctionParameters,
  StatusFilter,
  StructuredTableRow,
} from 'notebook/structured-tables/StructuredTableConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import ErrorIcon from 'ui-library/icons/ErrorIcon';

export interface TableValidationBypassTooltipProps<TTableMetadata, TRowMetadata> extends Pick<
  BaseActionFunctionParameters<TTableMetadata, TRowMetadata>,
  'structuredTable' | 'updateHandler'
> {
  errors: Array<string>;
  showFilterButton: boolean;
}

/**
 * A tooltip that displays validation errors for a structured table and
 * allows the user to filter the table to only show rows with errors.
 */
export default function TableValidationBypassTooltip<TTableMetadata, TRowMetadata>({
  errors,
  showFilterButton,
  structuredTable,
  updateHandler,
}: TableValidationBypassTooltipProps<TTableMetadata, TRowMetadata>): JSX.Element {
  const [filters, setFilters] = useState<Array<StatusFilter>>(structuredTable.filterInfo?.filters ?? []);

  useEffect(() => {
    setFilters(structuredTable.filterInfo?.filters ?? []);
  }, [structuredTable.filterInfo?.filters]);

  const isFilteringErrors = (filters: Array<StatusFilter>): boolean =>
    filters.includes(StatusFilter.HAS_ERROR);

  const _filterErrors = (_: React.MouseEvent): void => {
    let newFilters = filters;
    if (isFilteringErrors(filters)) {
      newFilters = []; // clear filters
    } else {
      newFilters = [StatusFilter.HAS_ERROR];
    }
    setFilters(newFilters);
    updateHandler({
      updatedCells: structuredTable.rows.map((row: StructuredTableRow<TRowMetadata>) => row.cells),
      columnUpdates: {},
      filters: newFilters,
    });
  };

  const uniqueErrors = useMemo(() => [...new Set(errors)], [errors]);

  return (
    <>
      <div className={css(styles.title)}>Resolve errors or submit and bypass validation</div>
      <div className={css(styles.description)}>
        {pluralize(uniqueErrors.length, 'Validation error', 'Multiple validation errors')} detected.
        {showFilterButton && ' Filter your view to address the errors and proceed.'}
      </div>
      <>
        {uniqueErrors.map((error, index) => (
          <div className={css(styles.errorRow)} key={index}>
            <div className={css(styles.errorIconColumn)}>
              <ErrorIcon className={css(styles.errorIcon)} />
            </div>
            <div className={css(styles.errorMessageColumn)}>
              <div
                className={css(styles.errorMessageText)}
                {...getTestProps({
                  component: 'TableValidationBypassTooltip',
                  element: 'errorRow',
                  index: `${index}`,
                })}
              >
                {error}
              </div>
            </div>
          </div>
        ))}
      </>
      {showFilterButton && (
        <div
          className={css(
            styles.filterRow,
            isFilteringErrors(filters) ? styles.filterRowActive : styles.filterRowInactive
          )}
          onClick={_filterErrors}
        >
          <Button
            icon="filter-menu"
            size={ButtonSize.MINI}
            buttonStyle={ButtonStyle.TRANSPARENT}
            className={css(filters.length ? styles.filterIconActive : styles.filterIconInactive)}
            dataTestAttrs={{component: 'TableValidationBypassTooltip', element: 'filterButton'}}
          />
          <span
            className={css(
              styles.filterText,
              filters.length ? styles.filterTextActive : styles.filterTextInactive
            )}
          >
            Filter to errors only
          </span>
        </div>
      )}
    </>
  );
}

export const styles = StyleSheet.create({
  title: {
    fontSize: '13px',
    fontWeight: 'bold',
    marginBottom: '2px',
  },
  description: {
    fontSize: '12px',
    lineHeight: '14px',
  },
  errorRow: {
    display: 'flex',
    margin: '5px 0 2px 0',
  },
  errorIconColumn: {
    margin: 'auto', // Center the icon
    flex: '0 0 20px', // Fixed width and flexible height
  },
  errorIcon: {
    color: Color.RED_400,
  },
  errorMessageColumn: {
    wordWrap: 'break-word', // Wrap text to next line
    flex: '1', // Flexible width
  },
  errorMessageText: {
    fontSize: '12px',
    lineHeight: '14px',
  },
  filterRow: {
    cursor: 'pointer',
    display: 'inline-flex',
    paddingRight: '5px',
    borderRadius: '6px',
  },
  filterRowActive: {
    backgroundColor: Color.BLUE_200,
  },
  filterRowInactive: {
    color: 'none',
  },
  filterIconActive: {
    color: Color.BLUE_600,
  },
  filterIconInactive: {
    color: Color.BLUE_500,
  },
  filterText: {
    fontWeight: 'bold',
    margin: '4px 0 0 2px',
  },
  filterTextActive: {
    color: Color.BLUE_600,
  },
  filterTextInactive: {
    color: Color.BLUE_500,
  },
});
